import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { FiBarChart2, FiList } from 'react-icons/fi';
import { CHART_FONT, CHART_TOOLTIP } from 'helpers/echartsTheme';

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const ContractMonthlyTrend = ({ data, loading, error, groupBy, onGroupByChange }) => {
  const [view, setView] = useState('chart');

  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-12"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading monthly trend</p>
    </Card>
  );

  const items = data || [];
  const chartH = 180;
  const barW   = groupBy === 'year' ? 72 : 44;
  const gap    = groupBy === 'year' ? 24 : 16;
  const svgW   = Math.max(items.length * (barW + gap) + gap, 320);
  const maxCount = Math.max(...items.map(d => (d.contract_count || 0) + (d.addendum_count || 0)), 1);
  const maxValue = Math.max(...items.map(d => d.total_value || 0), 1);

  const totals = items.reduce((s, d) => ({
    contract_count: s.contract_count + (d.contract_count || 0),
    addendum_count: s.addendum_count + (d.addendum_count || 0),
    total_value:    s.total_value    + (d.total_value    || 0),
    total_weight:   s.total_weight   + (d.total_weight   || 0),
  }), { contract_count: 0, addendum_count: 0, total_value: 0, total_weight: 0 });

  const chartOption = {
    textStyle: { fontFamily: CHART_FONT.fontFamily },
    grid: { left: 8, right: 8, top: 16, bottom: 0, containLabel: true },
    tooltip: {
      ...CHART_TOOLTIP,
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const item = items[params?.[0]?.dataIndex];
        if (!item) return '';
        return [
          `<b>${escapeHtml(item.month_name)}</b>`,
          `<span style="color:#2563EB">Contracts: ${item.contract_count || 0} — ${escapeHtml(fmtM(item.contract_value))}</span>`,
          `<span style="color:#D97706">Addendums: ${item.addendum_count || 0} — ${escapeHtml(fmtM(item.addendum_value))}</span>`,
          `<span style="color:#16A34A;font-weight:800">Total: ${escapeHtml(fmtM(item.total_value))}</span>`,
          `<span style="color:#9CA3AF">${Number(item.total_weight || 0).toFixed(1)} MT</span>`
        ].join('<br/>');
      }
    },
    xAxis: {
      type: 'category',
      data: items.map(d => d.month_name || ''),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        interval: 0,
        formatter: (value) => {
          const [first, second] = String(value).split(' ');
          return groupBy === 'year' ? `{yr|${first || ''}}` : `{p1|${first || ''}}\n{p2|${second || ''}}`;
        },
        rich: {
          yr: { color: '#6B7280', fontSize: 10, fontWeight: 700 },
          p1: { color: '#6B7280', fontSize: 9, align: 'center' },
          p2: { color: '#9CA3AF', fontSize: 8, align: 'center', padding: [2, 0, 0, 0] }
        }
      }
    },
    yAxis: [
      { type: 'value', show: false, max: maxCount },
      // Same headroom the SVG line had: it topped out 20px below the plot top.
      { type: 'value', show: false, max: maxValue * (chartH / (chartH - 20)) }
    ],
    series: [
      {
        name: 'Contracts',
        type: 'bar',
        stack: 'count',
        barWidth: barW,
        itemStyle: { color: '#3B82F6', opacity: 0.8, borderRadius: 2 },
        data: items.map(d => d.contract_count || 0)
      },
      {
        name: 'Addendums',
        type: 'bar',
        stack: 'count',
        itemStyle: { color: '#FBBF24', opacity: 0.85, borderRadius: 2 },
        data: items.map(d => d.addendum_count || 0)
      },
      {
        // Invisible stacked series carrying the total-count label above each stack.
        name: 'total-label',
        type: 'bar',
        stack: 'count',
        silent: true,
        itemStyle: { color: 'transparent' },
        tooltip: { show: false },
        label: {
          show: true,
          position: 'top',
          fontSize: 9,
          fontWeight: 600,
          color: '#374151',
          formatter: (p) => {
            const item = items[p.dataIndex];
            const totalCount = (item?.contract_count || 0) + (item?.addendum_count || 0);
            return (totalCount / maxCount) * chartH > 12 ? String(totalCount) : '';
          }
        },
        data: items.map(() => 0)
      },
      ...(items.length > 1 ? [{
        name: 'Total Value',
        type: 'line',
        yAxisIndex: 1,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { color: '#10B981', width: 2, opacity: 0.8 },
        itemStyle: { color: '#10B981', borderColor: '#ffffff', borderWidth: 1.5 },
        data: items.map(d => d.total_value || 0)
      }] : [])
    ]
  };

  return (
    <Card className="tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm">
      <div className="tw-px-5 tw-py-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
        <div className="tw-flex tw-items-center tw-gap-2">
          <FiBarChart2 size={16} className="tw-text-blue-500" />
          <h3 className="tw-font-bold tw-text-gray-900">
            {groupBy === 'year' ? 'Yearly Trend' : groupBy === 'quarter' ? 'Quarterly Trend' : 'Monthly Trend'}
          </h3>
          <span className="tw-text-xs tw-text-gray-400">Contracts &amp; Addendums over time · by contract date</span>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
          <div className="tw-flex tw-rounded tw-overflow-hidden tw-border tw-border-gray-200">
            <button type="button" onClick={() => setView('chart')}
              className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-cursor-pointer tw-border-0 ${view === 'chart' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50'}`}>Chart</button>
            <button type="button" onClick={() => setView('table')}
              className={`tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-cursor-pointer tw-border-0 tw-border-l tw-border-l-gray-200 ${view === 'table' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50'}`}><FiList size={11} /> Table</button>
          </div>
          <div className="tw-flex tw-rounded tw-overflow-hidden tw-border tw-border-gray-200">
            {['month', 'quarter', 'year'].map((g) => (
              <button
                key={g}
                onClick={() => onGroupByChange(g)}
                className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-colors tw-cursor-pointer ${
                  groupBy === g ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50'
                }`}
              >
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {view === 'table' ? (
        <div className="tw-overflow-auto tw-max-h-[420px]">
          <table className="tw-w-full tw-text-xs tw-border-collapse">
            <thead>
              <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10">
                <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-600">Period</th>
                <th className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-blue-600">Contracts</th>
                <th className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-amber-600">Addendums</th>
                <th className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-green-600">Total Value</th>
                <th className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-cyan-600">Weight (MT)</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={5} className="tw-text-center tw-text-gray-400 tw-py-8">No trend data available</td></tr>
              ) : items.map((d, i) => (
                <tr key={i} className={`tw-border-b tw-border-gray-100 ${i % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50/40'} hover:tw-bg-blue-50/40`}>
                  <td className="tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-800 tw-whitespace-nowrap">{d.month_name}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-text-blue-700">{d.contract_count || 0}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-amber-600">{d.addendum_count || 0}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-text-green-700">{fmtM(d.total_value)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-cyan-600">{Number(d.total_weight || 0).toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
            {items.length > 0 && (
              <tfoot>
                <tr className="tw-border-t-2 tw-border-gray-300 tw-bg-gray-100 tw-font-bold">
                  <td className="tw-px-3 tw-py-2 tw-text-gray-500 tw-text-xs">Total ({items.length})</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-blue-700">{totals.contract_count}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-amber-600">{totals.addendum_count}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-green-700">{fmtM(totals.total_value)}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-right tw-text-cyan-600">{totals.total_weight.toFixed(1)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ) : (
      <>
      {/* Legend */}
      <div className="tw-px-5 tw-pt-3 tw-flex tw-items-center tw-gap-4">
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <div className="tw-w-3 tw-h-3 tw-rounded-sm tw-bg-blue-500 tw-opacity-80" />
          <span className="tw-text-xs tw-text-gray-500">Contracts</span>
        </div>
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <div className="tw-w-3 tw-h-3 tw-rounded-sm tw-bg-amber-400 tw-opacity-80" />
          <span className="tw-text-xs tw-text-gray-500">Addendums</span>
        </div>
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <div className="tw-w-2 tw-h-2 tw-rounded-xl tw-bg-green-500" />
          <span className="tw-text-xs tw-text-gray-500">Total Value</span>
        </div>
      </div>

      <div className="tw-p-5 tw-overflow-x-auto">
        {items.length === 0 ? (
          <p className="tw-text-gray-400 tw-text-center tw-py-8">No trend data available</p>
        ) : (
          <ReactECharts
            option={chartOption}
            style={{ width: svgW, height: chartH + 50, minWidth: '100%' }}
            notMerge
          />
        )}
      </div>
      </>
      )}
    </Card>
  );
};

ContractMonthlyTrend.propTypes = {
  data:            PropTypes.array,
  loading:         PropTypes.bool,
  error:           PropTypes.any,
  groupBy:         PropTypes.oneOf(['month', 'quarter', 'year']),
  onGroupByChange: PropTypes.func
};
ContractMonthlyTrend.defaultProps = { data: [], loading: false, error: null, groupBy: 'month', onGroupByChange: () => {} };

export default ContractMonthlyTrend;
